import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AppLayoutComponent } from '../../layout/app-layout/app-layout.component';
import { ReqResService } from '../../service/req-res.service';
declare var $: any;
declare var toastr: any;

interface OrderModel {
  Id: number;
  OrderCode: string;
  User: string;
  Segment: string;
  TotalPrice: number;
  Role: number;
  UserId: number;
  Status: string;
  StatusCss: string;
  CreateDate: string;
  OrderNote: string;
}

interface OrderHistoryItem {
  actionType: string;
  title: string;
  actor: string;
  actionDate: string;
  note: string;
  cssClass: string;
  sortOrder: number;
}

interface OrderStatusCount {
  statusId: number;
  status: string;
  statusCss: string;
  count: number;
}

@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.css']
})
export class OrdersComponent implements OnInit {

  orders: OrderModel[] = [];
  filteredOrders: OrderModel[] = [];
  pagedOrders: OrderModel[] = [];

  searchText: string = '';
  currentPage: number = 1;
  pageSize: number = 50;
  totalPages: number = 1;
  totalOrderCount: number = 0;
  totalOrderPrice: number = 0;
  statusCounts: OrderStatusCount[] = [];
  selectedStatusId: number = 0;
  sortColumn: string = 'Id';
  sortDirection: 'asc' | 'desc' = 'desc';
  searchTimer: any = null;

  userRole: number = 0;
  userId: number = 0;

  orderLineData: any[] = [];
  orderDescription: string = '';
  orderCode: string = '';
  orderApprover: number = 0;
  orderStatus: number = 0;
  editingOrderId: number = 0;

  isLoading: boolean = false;
  isOrdersLoading: boolean = false;
  isApproving: boolean = false;
  isReleasing: boolean = false;
  isDeclining: boolean = false;

  selectedImage: string = '';
  private readonly defaultImage = '../assets/img/products/0.png';
  issuedByName: string = '';
  todayDate: string = '';

  declineReason: string = '';
  declineReasonTouched: boolean = false;
  historyOrderCode: string = '';
  historyData: OrderHistoryItem[] = [];
  isHistoryLoading: boolean = false;
  selectedNoteOrderCode: string = '';
  selectedOrderNote: string = '';

  constructor(
    private reqRes: ReqResService,
    private titleService: Title
  ) {
    this.titleService.setTitle('Orders');
  }

  ngOnInit(): void {
    this.todayDate = this.formatDate(new Date());
    this.loadOrders();
  }

  loadOrders(): void {
    this.isOrdersLoading = true;

    const url =
      '/Order/GetOrderListPaged' +
      '?page=' + this.currentPage +
      '&pageSize=' + this.pageSize +
      '&search=' + encodeURIComponent(this.searchText || '') +
      '&status=' + this.selectedStatusId +
      '&sortColumn=' + encodeURIComponent(this.sortColumn || 'Id') +
      '&sortDirection=' + encodeURIComponent(this.sortDirection || 'desc');

    this.reqRes.getData(url).subscribe(
      (data: any) => {
        const result = Array.isArray(data)
          ? { data: data, totalCount: data.length, totalPrice: 0, role: data[0]?.Role || 0, userId: data[0]?.UserId || 0 }
          : (data || {});

        this.orders = Array.isArray(result.data) ? result.data : [];
        this.filteredOrders = this.orders;
        this.pagedOrders = this.orders;

        this.totalOrderCount = Number(result.totalCount || 0);
        this.totalOrderPrice = Number(result.totalPrice || 0);
        this.statusCounts = Array.isArray(result.statusCounts) ? result.statusCounts : [];
        this.totalPages = Math.ceil(this.totalOrderCount / this.pageSize) || 1;
        this.userRole = Number(result.role || this.orders[0]?.Role || 0);
        this.userId = Number(result.userId || this.orders[0]?.UserId || 0);

        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages;
          this.loadOrders();
          return;
        }

        this.isOrdersLoading = false;
      },
      error => {
        this.isOrdersLoading = false;
        toastr.error('Oops! Something went wrong', '', { timeOut: 1500 });
        console.error(error);
      }
    );
  }

  applyFiltersAndPaging(): void {
    let temp = [...this.orders];

    if (this.searchText && this.searchText.trim() !== '') {
      const search = this.searchText.toLowerCase().trim();
      temp = temp.filter(x =>
        (x.OrderCode || '').toLowerCase().includes(search) ||
        (x.User || '').toLowerCase().includes(search) ||
        (x.Segment || '').toLowerCase().includes(search) ||
        (x.Status || '').toLowerCase().includes(search) ||
        (x.CreateDate || '').toLowerCase().includes(search) ||
        String(x.TotalPrice || '').toLowerCase().includes(search)
      );
    }

    temp = this.sortData(temp);
    this.filteredOrders = temp;
    this.totalPages = Math.ceil(this.filteredOrders.length / this.pageSize) || 1;

    if (this.currentPage > this.totalPages) {
      this.currentPage = 1;
    }

    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.pagedOrders = this.filteredOrders.slice(start, end);
  }

  sort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.currentPage = 1;
    this.loadOrders();
  }

  sortData(data: OrderModel[]): OrderModel[] {
    return data.sort((a: any, b: any) => {
      let valueA = a[this.sortColumn];
      let valueB = b[this.sortColumn];

      if (this.sortColumn === 'TotalPrice' || this.sortColumn === 'Id') {
        valueA = Number(valueA || 0);
        valueB = Number(valueB || 0);
      } else if (this.sortColumn === 'CreateDate') {
        valueA = this.parseDate(valueA);
        valueB = this.parseDate(valueB);
      } else {
        valueA = (valueA || '').toString().toLowerCase();
        valueB = (valueB || '').toString().toLowerCase();
      }

      if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  parseDate(dateStr: string): number {
    if (!dateStr) return 0;

    const parts = dateStr.split('/');
    if (parts.length !== 3) return 0;

    const day = Number(parts[0]);
    const month = Number(parts[1]) - 1;
    const year = Number(parts[2]);

    return new Date(year, month, day).getTime();
  }

  onSearchChange(): void {
    this.currentPage = 1;

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    this.searchTimer = setTimeout(() => {
      this.loadOrders();
    }, 350);
  }

  filterByStatus(statusId: number): void {
    this.selectedStatusId = Number(statusId || 0);
    this.currentPage = 1;
    this.loadOrders();
  }

  getStatusTotalCount(): number {
    return this.statusCounts.reduce((sum, x) => sum + Number(x.count || 0), 0);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.loadOrders();
  }

  get pages(): number[] {
    const maxVisible = 7;
    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, this.currentPage - half);
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array(end - start + 1).fill(0).map((_, i) => start + i);
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'fas fa-sort text-muted';
    return this.sortDirection === 'asc'
      ? 'fas fa-sort-up'
      : 'fas fa-sort-down';
  }

  deleteOrder(item: OrderModel): void {
    const confirmDelete = confirm(`${item.OrderCode} sifarişini silmək istəyirsən?`);
    if (!confirmDelete) return;

    toastr.warning(`Delete clicked: ${item.OrderCode}`, '', { timeOut: 1200 });
  }

  openEditPopup(orderId: number): void {
    this.editingOrderId = Number(orderId || 0);
    this.orderDescription = '';
    this.declineReason = '';
    this.declineReasonTouched = false;
    this.loadEditItems();
    $('#editOrderModal').modal('show');
  }

  openHistoryPopup(item: OrderModel): void {
    this.historyOrderCode = item?.OrderCode || '';
    this.historyData = [];
    this.isHistoryLoading = true;
    $('#orderHistoryModal').modal('show');

    this.reqRes.getData('/Order/GetOrderHistory/' + item.Id).subscribe(
      (data: any) => {
        this.historyData = Array.isArray(data) ? data : [];
        this.isHistoryLoading = false;
      },
      (error: any) => {
        this.isHistoryLoading = false;
        this.historyData = [];
        console.error(error);
        toastr?.error('Order history could not be loaded', '', { timeOut: 1200 });
      }
    );
  }

  openNotePopup(item: OrderModel): void {
    this.selectedNoteOrderCode = item?.OrderCode || '';
    this.selectedOrderNote = item?.OrderNote || '';
    $('#orderNoteModal').modal('show');
  }

  hasOrderNote(item: OrderModel): boolean {
    return !!(item && item.OrderNote && item.OrderNote.trim());
  }

  getHistoryIcon(actionType: string): string {
    if (actionType === 'submitted') return 'fas fa-paper-plane';
    if (actionType === 'approved') return 'fas fa-check';
    if (actionType === 'declined') return 'fas fa-times';
    if (actionType === 'completed') return 'fas fa-check-circle';
    return 'fas fa-circle';
  }
  requestedByName: string = '';
  loadEditItems(): void {
    this.isLoading = true;
    this.orderLineData = [];
    this.orderDescription = '';

    this.reqRes.getData('/Order/GetOrderDetails/' + this.editingOrderId).subscribe(
      (data: any) => {
        const order = data || {};
        console.log(order)
        const items = Array.isArray(order.items) ? order.items : [];

        this.orderLineData = items.map((x: any) => ({
          lineId: x.lineId,
          itemId: x.itemId,
          itemCode: x.itemCode,
          itemName: x.itemName,
          groupId: x.groupId,
          groupName: x.groupName,
          orderQty: Number(x.orderQty || 1),
          itemUnit: x.itemUnit,
          price: Number(x.price || 0),
          img: x.itemImg || this.defaultImage
        }));

        this.orderDescription = order.orderDesc || '';
        this.orderCode = order.orderCode || '';
        this.requestedByName = order.userName || '';
        this.orderApprover = Number(order.orderApprover || 0);
        this.orderStatus = Number(order.orderStatus || 0);

        this.issuedByName = (AppLayoutComponent._firstName + ' ' + AppLayoutComponent._lastName) || 'Warehouse Person Name';
        this.isLoading = false;
      },
      (error: any) => {
        this.isLoading = false;
        this.orderLineData = [];
        this.orderDescription = '';
        console.error(error);
        toastr?.error('Items could not be loaded', '', { timeOut: 1000 });
      }
    );
  }

  increaseQty(item: any): void {
    item.orderQty = Number(item.orderQty || 0) + 1;
  }

  decreaseQty(item: any): void {
    if (Number(item.orderQty || 0) > 1) {
      item.orderQty--;
    }
  }

  onQtyChange(item: any): void {
    item.orderQty = Number(item.orderQty || 1);

    if (isNaN(item.orderQty) || item.orderQty < 1) {
      item.orderQty = 1;
    }
  }

  removeItem(itemCode: string): void {
    this.orderLineData = this.orderLineData.filter(x => x.itemCode !== itemCode);
    toastr?.success('Item removed', '', { timeOut: 1000 });
  }

  get totalItems(): number {
    return new Set(this.orderLineData.map(x => x.itemCode)).size;
  }

  get totalQty(): number {
    return this.orderLineData.reduce((sum, item) => {
      return sum + Number(item.orderQty || 0);
    }, 0);
  }

  get totalPrice(): number {
    return this.orderLineData.reduce((sum, item) => {
      return sum + (Number(item.price || 0) * Number(item.orderQty || 0));
    }, 0);
  }

  get editStatusLabel(): string {
    if (this.orderStatus == 1) return 'Submitted';
    if (this.orderStatus == 2) return 'Approved';
    if (this.orderStatus == 3) return 'Completed';
    if (this.orderStatus == 4) return 'Declined';
    return 'Unknown';
  }

  get editStatusCss(): string {
    if (this.orderStatus == 1) return 'info';
    if (this.orderStatus == 2) return 'warning';
    if (this.orderStatus == 3) return 'success';
    if (this.orderStatus == 4) return 'danger';
    return 'secondary';
  }

  openDeclineModal(): void {
    this.declineReason = '';
    this.declineReasonTouched = false;
    ($('#declineReasonModal') as any).modal('show');
  }

  confirmDeclineOrder(): void {
    this.declineReasonTouched = true;

    if (!this.declineReason || !this.declineReason.trim()) {
      toastr?.warning('Decline reason is required', '', { timeOut: 1200 });
      return;
    }

    this.operationEditOrder('decline');
  }

  operationEditOrder(type: string): void {
    if (!this.orderLineData.length) {
      toastr?.warning('No item found', '', { timeOut: 1000 });
      return;
    }

    if (type === 'decline' && (!this.declineReason || !this.declineReason.trim())) {
      this.declineReasonTouched = true;
      toastr?.warning('Decline reason is required', '', { timeOut: 1200 });
      return;
    }

    const payload = {
      orderId: this.editingOrderId,
      totalItems: this.totalItems,
      totalQty: this.totalQty,
      totalPrice: this.totalPrice,
      orderDescription: this.orderDescription,
      type: type,
      declineReason: type === 'decline' ? this.declineReason.trim() : '',
      items: this.orderLineData.map(item => ({
        lineId: item.lineId,
        itemId: item.itemId,
        itemCode: item.itemCode,
        itemName: item.itemName,
        itemUnit: item.itemUnit,
        groupId: item.groupId,
        groupName: item.groupName,
        orderQty: Number(item.orderQty || 0),
        price: Number(item.price || 0),
        amount: Number(item.price || 0) * Number(item.orderQty || 0),
        itemImg: item.img
      }))
    };

    if (type === 'approve') {
      this.isApproving = true;
    } else if (type === 'complete') {
      this.isReleasing = true;
    } else if (type === 'decline') {
      this.isDeclining = true;
    }

    this.reqRes.postData('/Order/OperationOrder', payload).subscribe(
      (res: any) => {
        if (type === 'approve') {
          this.isApproving = false;
          toastr?.success('Order Approved Successfully', '', { timeOut: 1000 });
        } else if (type === 'complete') {
          this.isReleasing = false;
          toastr?.success('Order Completed Successfully', '', { timeOut: 1000 });
          this.orderStatus = 3;
        } else if (type === 'decline') {
          this.isDeclining = false;
          this.orderStatus = 4;
          ($('#declineReasonModal') as any).modal('hide');
          ($('#editOrderModal') as any).modal('hide');
          this.declineReason = '';
          this.declineReasonTouched = false;
          toastr?.success('Order Declined Successfully', '', { timeOut: 1000 });
        }

        this.loadOrders();
      },
      (error: any) => {
        this.isApproving = false;
        this.isReleasing = false;
        this.isDeclining = false;
        console.error('ApplyEditOrder error:', error);
        toastr?.error('Order update failed', '', { timeOut: 1000 });
      }
    );
  }

  printDocument(): void {
    const printContents = document.getElementById('printArea')?.innerHTML;
    if (!printContents) {
      toastr?.error('Print content not found', '', { timeOut: 1200 });
      return;
    }

    const popup = window.open('', '_blank', 'width=1200,height=900');
    if (!popup) {
      toastr?.error('Popup blocked by browser', '', { timeOut: 1500 });
      return;
    }

    popup.document.open();
    popup.document.write(`
      <html>
        <head>
          <title>Print Delivery Form</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              padding: 24px;
              font-family: Arial, Helvetica, sans-serif;
              color: #111827;
              background: #ffffff;
            }
            .print-sheet {
              width: 210mm;
              min-height: 297mm;
              margin: 0 auto;
              background: #ffffff;
              padding: 11mm 7mm;
            }
            .header {
              text-align: center;
              margin-bottom: 28px;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              font-weight: 700;
              letter-spacing: 0.4px;
              text-transform: uppercase;
            }
            .header p {
              margin: 8px 0 0;
              font-size: 14px;
              color: #4b5563;
            }
            .meta-table, .items-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 24px;
            }
            .meta-table td {
              padding: 7px 10px;
              border: 1px solid #d1d5db;
              font-size: 14px;
              vertical-align: top;
            }
            .meta-label {
              width: 150px;
              background: #f9fafb;
              font-weight: 700;
            }
            .section-title {
              margin: 26px 0 12px;
              font-size: 16px;
              font-weight: 700;
              color: #111827;
            }
            .items-table th,
            .items-table td {
              border: 1px solid #cfd5dd;
              padding: 10px 12px;
              font-size: 14px;
            }
            .items-table th {
              background: #eef2f7;
              text-align: left;
              font-weight: 700;
            }
            .items-table td.center,
            .items-table th.center {
              text-align: center;
            }
            .items-table td.right,
            .items-table th.right {
              text-align: right;
            }
            .confirmation-text {
              font-size: 14px;
              line-height: 1.8;
              margin: 18px 0 30px;
            }
            .signature-grid {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            .signature-grid td {
              width: 50%;
              vertical-align: top;
              padding: 12px 10px 0 0;
            }
            .signature-card {
              min-height: 150px;
            }
            .signature-title {
              font-size: 15px;
              font-weight: 700;
              margin-bottom: 18px;
            }
            .sign-line {
              margin-bottom: 18px;
              font-size: 14px;
              line-height: 1.8;
            }
            .line {
              display: inline-block;
              min-width: 220px;
              border-bottom: 1px solid #111827;
              min-height: 20px;
              vertical-align: bottom;
            }
            .footer {
              margin-top: 34px;
              font-size: 12px;
              color: #6b7280;
              text-align: center;
            }
            @page {
              size: A4;
              margin: 10mm;
            }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);
    popup.document.close();
    popup.focus();

    setTimeout(() => {
      popup.print();
      popup.close();
    }, 500);
  }

  formatDate(date: Date): string {
    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  trackByItem(index: number, item: any): any {
    return item?.lineId || item?.itemCode || index;
  }

  openImagePreview(img: string): void {
    this.selectedImage = img || this.defaultImage;
    ($('#imagePreviewModal') as any).modal('show');
  }

  onImageError(event: any): void {
    if (event?.target && event.target.src !== this.defaultImage) {
      event.target.src = this.defaultImage;
    }
  }
}
